import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { resetPassword } from "../../store";
import { useThunk } from "../../hooks/useThunk";
import useForm from "../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import CustomInput from "../../components/Form/Input";
import CustomButton from "../../components/Form/CustomButton";
import showToast from "../../utils/toastNotifications";

const COOLDOWN_SECONDS = 30;

const ForgotPassword = () => {
  const [doResetPassword, isResettingPassword] = useThunk(resetPassword);

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    clearError,
  } = useForm(
    {
      email: "",
    },
    {
      email: true,
    },
    {
      email: COMMON_FIELD_TYPES.EMAIL_FIELD,
    }
  );

  const [cooldownLeft, setCooldownLeft] = useState(0);

  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const id = setInterval(() => {
      setCooldownLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownLeft]);

  const isEmailValid = useMemo(
    () => !!formData.email && !errors.email,
    [formData.email, errors.email]
  );

  const isButtonDisabled =
    !isEmailValid || isResettingPassword || cooldownLeft > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true });

    const isValid = validateForm();
    if (isValid) {
      const result = await doResetPassword(formData);
      if (result?.success) {
        showToast("success", result?.response?.message || "Reset link sent");
        setCooldownLeft(COOLDOWN_SECONDS);
      } else {
        showToast("error", result?.response?.message || "Failed to send link");
      }
    }
  };

  return (
    <div className="w-full max-w-[516px] bg-white/80 p-6 rounded-[20px] shadow-[0px_4px_4px_rgba(0,0,0,0.25)]">
      <form className="flex flex-col" onSubmit={handleSubmit}>
        <h1 className="text-level-4 sm:text-[30px] font-semibold text-[#4772F1] font-alexandria">
          Forgot Password?
        </h1>

        <p className="mt-[50px] text-[17px] text-[#071952] font-alexandria">
          Enter your email address and we will send you a link to reset your
          password.
        </p>

        <CustomInput
          label="E-mail Address"
          type="email"
          name="email"
          placeholder="Enter your email address"
          isRequired={true}
          value={formData.email}
          error={touched.email && errors.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          onBlur={() => handleBlur("email", formData.email)}
          onFocus={() => clearError("email")}
          labelStyle="font-alexandria font-medium sm:text-[16px] text-[#0F161E] mt-[31px]"
          className="h-[54px] mt-[12px] px-3 rounded-xl border border-gray-300 bg-primary-light/30 text-content placeholder-[#9D9D9D] placeholder:text-[16px] placeholder:leading-[20px] placeholder:font-normal"
          inputStyle="bg-[#4772F1] bg-opacity-[0.03]"
        />

        <CustomButton
          type="submit"
          loading={isResettingPassword}
          disabled={isButtonDisabled}  
          className="h-[60px] sm:h-[60px] w-full mt-[80px] sm:mt-[156.5px] rounded-xl text-white font-inter text-[18px] font-semibold bg-gradient-to-b from-[#4772F1] to-[#873FE2]"
          buttonText={
            cooldownLeft > 0
              ? `Resend in ${cooldownLeft}s`
              : "Send Reset Link"
          }
        />

        <div className="mt-[13px] text-center text-[15px] font-medium font-inter">
          <span className="text-[#00000080]">Have an account? </span>
          <Link to="/signin">
            <span className="text-[#4772F1] cursor-pointer underline">
              Login
            </span>
          </Link>
        </div>
      </form>
    </div>
  );
};

export default ForgotPassword;
